'use client';

import { Suspense } from 'react';

import { useIsMobile } from '@kit/ui/use-mobile';

import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import { useIsNative } from '~/(tenant)/_lib/capacitor/capacitor-context';
import {
  type SectionConfig,
  parseNativeHomeConfig,
} from '~/(tenant)/_lib/schema/native-home-config.schema';
import type { GreetingPrefetch } from '~/(tenant)/_lib/server/load-greeting-data';
import type { NativeClassCategory } from '~/(tenant)/_lib/server/load-native-class-categories';
import type { NativeCoach } from '~/(tenant)/_lib/server/load-native-coaches';
import type { NativeSport } from '~/(tenant)/_lib/server/load-native-sports';
import { SportsSection } from './sections/sports-section';

import { ActivitySection } from './sections/activity-section';
import { BookingCategorySection } from './sections/booking-category-section';
import { ClassCategoriesSection } from './sections/class-categories-section';
import { CoachesSection } from './sections/coaches-section';
import { GreetingSection } from './sections/greeting-section';
import { GuestAllowanceSection } from './sections/guest-allowance-section';
import { HeroSection } from './sections/hero-section';
import {
  type LocationData,
  LocationSection,
} from './sections/location-section';
import { MemberIdCardSection } from './sections/member-id-card-section';
import { MyBookingsSection } from './sections/my-bookings-section';
import { MyMembershipsSection } from './sections/my-memberships-section';
import { NumberedListSection } from './sections/numbered-list-section';
import {
  HERO_OVERLAP_PX,
  QuickActionsSection,
} from './sections/quick-actions-section';
import { QuoteSection } from './sections/quote-section';
import { RecentGuestPassesSection } from './sections/recent-guest-passes-section';
import { StatsStripSection } from './sections/stats-strip-section';
import { TodayClassesSection } from './sections/today-classes-section';

interface NativeHomeRendererProps {
  timeZone?: string;
  /** Raw public_data.native_home from the accounts table */
  rawConfig: unknown;
  accountId: string;
  accountSlug: string;
  /** Tenant locations from booking types data */
  locations?: LocationData[];
  /** UUID of the active venue resolved server-side from the URL/cookie. Threaded
   *  down to location-aware sections (coaches, today-classes) so they fetch
   *  scoped data instead of showing every venue's content mixed together. */
  activeLocationId?: string | null;
  /**
   * Server-prefetched greeting data (name + credits + tier). When provided,
   * the GreetingSection hydrates synchronously and never shows the
   * "Welcome → Welcome back, X" flash on first render.
   */
  greetingPrefetch?: GreetingPrefetch | null;
  /**
   * Server-prefetched coaches list. Required because the browser anon client
   * has no SELECT grant on `public.trainers` — fetching client-side returns
   * zero rows for unauthenticated visitors.
   */
  coachesPrefetch?: NativeCoach[];
  sportsPrefetch?: NativeSport[];
  /**
   * Class types referenced by `class-categories` sections. Prefetched for the
   * same anon-grant reason as coaches — see load-native-class-categories.ts.
   */
  classCategoriesPrefetch?: NativeClassCategory[];
  /** Fallback content to show when not in native/mobile mode. Null on Capacitor (server-detected). */
  children: React.ReactNode;
  /**
   * Server-detected Capacitor request (cookie or native UA token). When true
   * the home screen is rendered on the SERVER — previously the gate below was
   * client-state-only, so native SSR emitted the web landing page and swapped
   * to the home screen only after hydration.
   */
  serverIsNative?: boolean;
}

export function NativeHomeRenderer({
  timeZone,
  rawConfig,
  accountId,
  accountSlug,
  locations = [],
  activeLocationId = null,
  greetingPrefetch = null,
  coachesPrefetch = [],
  sportsPrefetch = [],
  classCategoriesPrefetch = [],
  children,
  serverIsNative = false,
}: NativeHomeRendererProps) {
  const { isNative, isReady } = useIsNative();
  const isMobile = useIsMobile();
  const t = useNativeTokens();

  // Show the config-driven home screen for:
  // 1. Capacitor native app — server-detected, so it's already in the SSR HTML
  //    (no post-hydration landing→home swap, and the splash can lift at first
  //    paint instead of after hydration)
  // 2. Mobile web viewport (when ios_app_enabled — the parent only renders this component when enabled)
  // Desktop always gets the landing page (children)
  //
  // serverIsNative deliberately wins over the client-side check and is never
  // "corrected" after hydration: when the server detected native, the page
  // passed children=null (no landing page was rendered), so falling back to
  // children on a serverIsNative/!isNative mismatch would blank the screen.
  // The only way to hit that mismatch is spoofing the ZameelNative UA token in
  // a regular browser — self-inflicted, exposes nothing (it's the same screen
  // any mobile-web viewport gets; sections gate their own data on auth), and
  // doubles as a handy way to preview the native home from a desktop browser.
  const showHomeScreen = serverIsNative || (isReady && (isNative || isMobile));

  if (!showHomeScreen) {
    return <>{children}</>;
  }

  const config = parseNativeHomeConfig(rawConfig);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 'calc(100vh - 4rem)',
        paddingBottom: 80,
        backgroundColor: t.background,
        color: t.foreground,
      }}
    >
      {config.sections.map((section, index) => (
        <Suspense
          key={`${section.type}-${index}`}
          fallback={<SectionSkeleton />}
        >
          <SectionRenderer
            timeZone={timeZone}
            section={section}
            previous={config.sections[index - 1]}
            next={config.sections[index + 1]}
            accountId={accountId}
            accountSlug={accountSlug}
            locations={locations}
            activeLocationId={activeLocationId}
            greetingPrefetch={greetingPrefetch}
            coachesPrefetch={coachesPrefetch}
            sportsPrefetch={sportsPrefetch}
            classCategoriesPrefetch={classCategoriesPrefetch}
          />
        </Suspense>
      ))}

      {/* Splash hiding moved to the page shell (apps/web/app/(tenant)/page.tsx):
          with the page streamed behind Suspense, the shell's inline script
          lifts the splash at skeleton paint — well before this component's
          HTML arrives. */}
    </div>
  );
}

/** A quick-actions bar floats over a hero placed directly above it. */
function isActionBar(section: SectionConfig | undefined): boolean {
  return section?.type === 'quick-actions' && section.layout === 'bar';
}

function SectionRenderer({
  timeZone,
  section,
  previous,
  next,
  accountId,
  accountSlug,
  locations,
  activeLocationId,
  greetingPrefetch,
  coachesPrefetch,
  sportsPrefetch,
  classCategoriesPrefetch,
}: {
  timeZone?: string;
  section: SectionConfig;
  previous: SectionConfig | undefined;
  next: SectionConfig | undefined;
  accountId: string;
  accountSlug: string;
  locations: LocationData[];
  activeLocationId: string | null;
  greetingPrefetch: GreetingPrefetch | null;
  coachesPrefetch: NativeCoach[];
  sportsPrefetch: NativeSport[];
  classCategoriesPrefetch: NativeClassCategory[];
}) {
  switch (section.type) {
    case 'hero': {
      const hero = (
        <HeroSection
          config={section}
          accountId={accountId}
          activeLocationId={activeLocationId}
        />
      );

      if (!isActionBar(next)) return hero;

      // Lift the hero's headline and dots clear of the overlapping bar — only
      // when the bar actually rendered next to it (it can render nothing once
      // capability/venue filters drop every action).
      return (
        <div
          className="[&:has(+[data-overlaps-hero])]:[--native-hero-bottom-inset:var(--native-hero-overlap)]"
          style={
            {
              '--native-hero-overlap': `${HERO_OVERLAP_PX}px`,
            } as React.CSSProperties
          }
        >
          {hero}
        </div>
      );
    }

    case 'location':
      return <LocationSection locations={locations} />;

    case 'quick-actions':
      return (
        <QuickActionsSection
          config={section}
          accountId={accountId}
          activeLocationId={activeLocationId}
          overlapsHero={isActionBar(section) && previous?.type === 'hero'}
        />
      );

    case 'booking-category':
      return (
        <BookingCategorySection
          config={section}
          accountSlug={accountSlug}
          locationId={activeLocationId}
        />
      );

    case 'my-bookings':
      return <MyBookingsSection accountId={accountId} />;

    case 'today-classes':
      return (
        <TodayClassesSection
          timeZone={timeZone}
          accountSlug={accountSlug}
          locationId={activeLocationId}
        />
      );

    case 'coaches':
      return <CoachesSection coaches={coachesPrefetch} />;
    case 'sports':
      return <SportsSection config={section} sports={sportsPrefetch} accountId={accountId} />;

    case 'class-categories':
      return (
        <ClassCategoriesSection
          config={section}
          classTypes={classCategoriesPrefetch}
        />
      );

    case 'greeting':
      return (
        <GreetingSection
          config={section}
          accountId={accountId}
          prefetch={greetingPrefetch}
        />
      );

    case 'stats-strip':
      return <StatsStripSection config={section} accountId={accountId} />;

    case 'numbered-list':
      return <NumberedListSection config={section} accountId={accountId} />;

    case 'quote':
      return <QuoteSection config={section} />;

    case 'member-id-card':
      return <MemberIdCardSection accountId={accountId} />;

    case 'guest-allowance':
      return <GuestAllowanceSection accountId={accountId} />;

    case 'my-memberships':
      return <MyMembershipsSection config={section} accountId={accountId} />;

    case 'recent-guest-passes':
      return <RecentGuestPassesSection accountId={accountId} />;

    case 'activity':
      return <ActivitySection config={section} accountId={accountId} />;

    default:
      return null;
  }
}

function SectionSkeleton() {
  const t = useNativeTokens();
  return (
    <div
      style={{
        marginLeft: 18,
        marginRight: 18,
        marginTop: 16,
        height: 128,
        borderRadius: t.radius,
        backgroundColor: t.line,
      }}
    />
  );
}
